import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface RenameAssessmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  defaultName?: string;
  onSave: (newName: string) => Promise<boolean | void> | boolean | void;
  title?: string;
  description?: string;
}

export default function RenameAssessmentDialog({
  open,
  onOpenChange,
  currentName,
  defaultName,
  onSave,
  title = 'Rename assessment',
  description = 'Give this assessment a name so you can easily identify it later.',
}: RenameAssessmentDialogProps) {
  const [value, setValue] = useState(currentName || defaultName || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(currentName || defaultName || '');
  }, [open, currentName, defaultName]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const saved = await onSave(trimmed);
      if (saved !== false) {
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assessment-name">Assessment name</Label>
            <Input
              id="assessment-name"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={defaultName || 'e.g. Q2 Engineering Review'}
              maxLength={120}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !value.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
